import React, { useState, useEffect } from 'react';
import { FiSearch, FiX, FiEdit, FiTrash2, FiMessageSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  setFilters,
  clearError,
} from '../store/slices/customersSlice';
import { fetchUnreadCount } from '../store/slices/conversationsSlice';
import { toast } from 'react-toastify';
import CustomerModal from '../components/CustomerModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { applyPrefixToContacts, buildGoogleAuthUrl, buildGoogleContactsApiUrl, getSelectedGoogleContacts, normalizeGoogleContacts } from '../utils/googleContacts';
import { apiService } from '../services/api';
import './Customers.css';

const CUSTOMER_PAGE_COOKIE = 'customers_current_page';

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookieValue = (name, value, days = 30) => {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax`;
};

const Customers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customers, loading, error, filters, total, pagination } = useSelector(state => state.customers);
  const { unreadCount } = useSelector(state => state.conversations);
  const { businessAccountId } = useSelector(state => state.auth);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportingContacts, setIsImportingContacts] = useState(false);
  const [previewContacts, setPreviewContacts] = useState([]);
  const [selectedPreviewContacts, setSelectedPreviewContacts] = useState([]);
  const [importPrefix, setImportPrefix] = useState('');
  const [googleContactsAccessToken, setGoogleContactsAccessToken] = useState('');
  const [googleContactsNextPageToken, setGoogleContactsNextPageToken] = useState('');
  const [isLoadingNextContacts, setIsLoadingNextContacts] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = getCookieValue(CUSTOMER_PAGE_COOKIE);
    const parsedPage = Number(savedPage);
    return Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  });

  useEffect(() => {
    setCookieValue(CUSTOMER_PAGE_COOKIE, currentPage);
  }, [currentPage]);

  // Fetch customers on component mount
  useEffect(() => {
    const limit = pagination?.limit ?? 100;
    dispatch(fetchCustomers({ page: currentPage, limit }))
      .unwrap()
      .catch(err => {
        toast.error(err?.message || 'Failed to fetch customers');
      });
  }, [dispatch, currentPage]);
 
  const searchHandler = async () => {
        dispatch(fetchCustomers({page: 1, limit : 100, search: searchQuery }));
  }
  const clearSearch = () => {
    setSearchQuery('');
    dispatch(setFilters({ search: '', status: selectedFilter }));
    dispatch(fetchCustomers({ page: 1, limit: 100 }));
  }
  // Fetch unread conversation count
  useEffect(() => {
    if (businessAccountId) {
      dispatch(fetchUnreadCount({ businessAccountId }))
        .unwrap()
        .catch(err => {
          console.warn('Failed to fetch unread count:', err);
        });
    }
  }, [dispatch, businessAccountId]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error?.message || 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phone?.includes(searchQuery) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      selectedFilter === 'all' ||
      (selectedFilter === 'active' && customer.status === 'active') ||
      (selectedFilter === 'inactive' && customer.status === 'inactive') ||
      (selectedFilter === 'unread' && customer.unreadCount > 0);

    return matchesSearch && matchesFilter;
  });

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleChatClick = (customerId) => {
    navigate(`/conversations/${customerId}`);
  };

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const fetchGoogleContactsPage = async (accessToken, pageToken = '') => {
    const response = await fetch(buildGoogleContactsApiUrl(accessToken, pageToken));
    if (!response.ok) {
      throw new Error('Unable to fetch Google Contacts right now.');
    }

    return response.json();
  };

  const handleGoogleContactsAuthResult = (payload) => {
    if (payload?.error) {
      setIsImportingContacts(false);
      toast.error('Google Contacts authorization was cancelled or failed.');
      return;
    }

    const accessToken = payload?.accessToken;
    if (!accessToken) {
      setIsImportingContacts(false);
      toast.error('Google Contacts access could not be initialized.');
      return;
    }

    fetchGoogleContactsPage(accessToken)
      .then((response) => {
        const normalized = normalizeGoogleContacts(response.connections || []);
        setGoogleContactsAccessToken(accessToken);
        setGoogleContactsNextPageToken(response.nextPageToken || '');
        setPreviewContacts(normalized);
        setSelectedPreviewContacts(normalized.map((contact) => contact.id));
        if (normalized.length === 0) {
          toast.info('No contacts were found in Google Contacts.');
        } else {
          toast.success(`Ready to save ${normalized.length} contact${normalized.length > 1 ? 's' : ''} from Google Contacts.`);
        }
      })
      .catch(() => {
        toast.error('Unable to fetch Google Contacts right now.');
      })
      .finally(() => {
        setIsImportingContacts(false);
      });
  };

  useEffect(() => {
    const pendingAuthResult = window.localStorage.getItem('googleContactsAuthResult');
    if (!pendingAuthResult) return;

    window.localStorage.removeItem('googleContactsAuthResult');

    try {
      handleGoogleContactsAuthResult(JSON.parse(pendingAuthResult));
    } catch (err) {
      console.warn('Failed to process pending Google Contacts auth result:', err);
      toast.error('Unable to load Google Contacts right now.');
      setIsImportingContacts(false);
    }
  }, []);

  const handleImportFromGoogleContacts = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    const redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google-callback`;

    if (!clientId) {
      toast.info('Google client ID is not configured yet. Please add REACT_APP_GOOGLE_CLIENT_ID to your environment.');
      return;
    }
    console.log('Initiating Google Contacts import with clientId:', clientId, 'and redirectUri:', redirectUri);
    setIsImportingContacts(true);
    const popup = window.open(
      buildGoogleAuthUrl(clientId, redirectUri),
      'googleContactsAuth',
      'width=520,height=700,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      setIsImportingContacts(false);
      toast.error('Popup blocked. Please allow popups for Google Contacts import.');
      return;
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'google-contacts-token') return;

      window.removeEventListener('message', handleMessage);
      handleGoogleContactsAuthResult(event.data);
    };

    const handleStorage = (event) => {
      if (event.key !== 'googleContactsAuthResult' || !event.newValue) return;
      window.removeEventListener('storage', handleStorage);
      try {
        handleGoogleContactsAuthResult(JSON.parse(event.newValue));
      } catch (err) {
        console.warn('Failed to process Google Contacts storage payload:', err);
        toast.error('Unable to load Google Contacts right now.');
        setIsImportingContacts(false);
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
  };

  const handleImportPreviewSave = async () => {
    const selectedContacts = getSelectedGoogleContacts(previewContacts, selectedPreviewContacts);
    if (!selectedContacts.length) {
      toast.info('Select at least one contact to import.');
      return;
    }

    try {
      const payload = applyPrefixToContacts(selectedContacts, importPrefix);
      const response = await apiService.customers.import(payload);
      const importedCount = response?.data?.data?.totalImported || selectedContacts.length;
      toast.success(`Saved ${importedCount} imported contact${importedCount > 1 ? 's' : ''}`);
      setPreviewContacts([]);
      setSelectedPreviewContacts([]);
      setImportPrefix('');
      setGoogleContactsAccessToken('');
      setGoogleContactsNextPageToken('');
      // Reset to page 1 to show newly imported contacts
      setCurrentPage(1);
      dispatch(fetchCustomers({ page: 1, limit: 100 }));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to import contacts');
    }
  };

  const handleAddNextGoogleContacts = async () => {
    if (!googleContactsAccessToken || !googleContactsNextPageToken || isLoadingNextContacts) return;

    setIsLoadingNextContacts(true);

    try {
      const response = await fetchGoogleContactsPage(googleContactsAccessToken, googleContactsNextPageToken);
      const normalized = normalizeGoogleContacts(response.connections || []);

      if (!normalized.length) {
        setGoogleContactsNextPageToken(response.nextPageToken || '');
        toast.info('No additional Google contacts were found.');
        return;
      }

      // Replace previous preview with the next batch (clear previous 100)
      setPreviewContacts(normalized);
      setSelectedPreviewContacts(normalized.map((contact) => contact.id));
      setGoogleContactsNextPageToken(response.nextPageToken || '');
      toast.success(`Loaded ${normalized.length} contacts (previous preview cleared).`);
    } catch (err) {
      toast.error(err?.message || 'Unable to load more Google Contacts right now.');
    } finally {
      setIsLoadingNextContacts(false);
    }
  };

  const togglePreviewContactSelection = (contactId) => {
    setSelectedPreviewContacts((current) => {
      if (current.includes(contactId)) {
        return current.filter((id) => id !== contactId);
      }

      return [...current, contactId];
    });
  };

  const handleSelectAllContacts = () => {
    setSelectedPreviewContacts(previewContacts.map((contact) => contact.id));
  };

  const handleDeselectAllContacts = () => {
    setSelectedPreviewContacts([]);
  };

  const handleCancelImport = () => {
    setPreviewContacts([]);
    setSelectedPreviewContacts([]);
    setImportPrefix('');
    setGoogleContactsAccessToken('');
    setGoogleContactsNextPageToken('');
    toast.info('Google Contacts import cancelled.');
  };

  const handleEditCustomer = (e, customer) => {
    e.stopPropagation();
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleDeleteCustomer = (e, customer) => {
    e.stopPropagation();
    setCustomerToDelete(customer);
    setIsDeleteModalOpen(true);
  };

  const handleSaveCustomer = async (formData) => {
    try {
      if (editingCustomer) {
        // Update existing customer
        await dispatch(updateCustomer({ 
          id: editingCustomer._id, 
          data: formData 
        })).unwrap();
        toast.success('Customer updated successfully');
      } else {
        // Add new customer
        await dispatch(createCustomer(formData)).unwrap();
        toast.success('Customer created successfully');
      }
      setIsModalOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to save customer');
    }
  };

  const handleConfirmDelete = async () => {
    if (customerToDelete) {
      try {
        await dispatch(deleteCustomer(customerToDelete._id)).unwrap();
        toast.success('Customer deleted successfully');
        setIsDeleteModalOpen(false);
        setCustomerToDelete(null);
      } catch (err) {
        toast.error(err?.message || 'Failed to delete customer');
      }
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`status-badge ${status}`}>
        {status === 'active' ? '●' : '○'} {status}
      </span>
    );
  };

  return (
    <div className="customers-container">
      <div className="customers-header">
        <div className="header-content">
          <h1>Customers</h1>
          <p>Manage your customer conversations</p>
        </div>
        <div className="customers-header-actions">
          <button className="btn-secondary" onClick={handleImportFromGoogleContacts} disabled={isImportingContacts}>
            {isImportingContacts ? 'Connecting…' : 'Import from Google Contacts'}
          </button>
          <button className="btn-add-customer" onClick={handleAddCustomer}>
            <span>+</span> Add Customer
          </button>
        </div>
      </div>

      <div className="customers-toolbar">
        <div className="search-box">
          <div className="input-group">
            <FiSearch className="input-icon" />
            <input
              type="text"
              placeholder="Search customers by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  dispatch(setFilters({ search: searchQuery, status: selectedFilter }));
                }
              }}
              className="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="input-clear-btn"
                onClick={() => {setSearchQuery(''); clearSearch();}}
                aria-label="Clear search"
              >
                <FiX />
              </button>
            )}
            <button
              type="button"
              className="btn-add-customer"
              onClick={searchHandler}
              aria-label="Search customers"
              title="Search"
            >
              <FiSearch size={16} />
            </button>
          </div>
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('all')}
          >
            All ({total})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'active' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('active')}
          >
            Active ({total})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'inactive' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('inactive')}
          >
            Inactive ({customers.filter(c => c.status === 'inactive').length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'unread' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('unread')}
          >
            Unread ({customers.filter(c => c.unreadCount > 0).length})
          </button>
        </div>
      </div>

      {previewContacts.length > 0 && (
        <div className="import-preview-card">
          <div className="import-prefix-group">
            <label htmlFor="importPrefix">Prefix for all selected contacts</label>
            <input
              id="importPrefix"
              type="text"
              value={importPrefix}
              onChange={(e) => setImportPrefix(e.target.value)}
              placeholder="e.g. Dr, Mr, Ms"
            />
            <p className="import-prefix-help">Enter one prefix here and it will be applied to every selected Google contact as a group.</p>
          </div>
          <div className="import-preview-header">
            <div>
              <h3>Google Contacts Preview</h3>
              <p>{selectedPreviewContacts.length} of {previewContacts.length} contact{previewContacts.length > 1 ? 's' : ''} selected</p>
            </div>
            <div className="import-preview-actions">
              <button className="btn-secondary" onClick={handleSelectAllContacts} disabled={selectedPreviewContacts.length === previewContacts.length}>
                Select All
              </button>
              <button className="btn-secondary" onClick={handleDeselectAllContacts} disabled={selectedPreviewContacts.length === 0}>
                Deselect All
              </button>
              {googleContactsNextPageToken && (
                <button className="btn-secondary" onClick={handleAddNextGoogleContacts} disabled={isLoadingNextContacts}>
                  {isLoadingNextContacts ? 'Loading…' : 'Load Next 100'}
                </button>
              )}
              <button className="btn-add-customer" onClick={handleImportPreviewSave} disabled={!selectedPreviewContacts.length}>
                Save Selected ({selectedPreviewContacts.length})
              </button>
              <button className="btn-secondary" onClick={handleCancelImport}>
                Cancel
              </button>
            </div>
          </div>
          <div className="import-preview-list">
            {previewContacts.map((contact) => {
              const isSelected = selectedPreviewContacts.includes(contact.id);
              const displayPrefix = importPrefix?.trim() || contact.prefix || '';
              const displayName = displayPrefix ? `${displayPrefix} ${contact.name}` : (contact.name || 'Unnamed contact');
              return (
                <label className="import-preview-item" key={contact.id}>
                  <div className="import-preview-item-top">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePreviewContactSelection(contact.id)}
                    />
                    <strong>{displayName}</strong>
                  </div>
                  <span>{contact.phone || 'No phone'}</span>
                  <span>{contact.email || 'No email'}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="customers-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No customers found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          filteredCustomers.map(customer => (
            <div
              key={customer.id}
              className="customer-card"
              onClick={() => handleCustomerClick(customer._id)}
            >
            
              <div className="customer-info">
                <div className="customer-main-line">
                  <h3 className="customer-name truncate">
                    {customer?.prefix?.trim() && (
                        <span className="customer-prefix">{customer.prefix.trim()}</span>
                    )}
                    {customer.name || 'Unnamed'}
                  </h3>
                 
                  <span className="customer-phone truncate">{customer.phoneNumber || customer.phone}</span>
                </div>
                <div className="customer-sub-line">
                  <span className="customer-email truncate">{customer.email || ''}</span>
                  {customer.lastMessage && (
                    <span className="customer-last-message truncate">{customer.lastMessage}</span>
                  )}
                </div>
                
              </div>
              <div className="customer-meta">
                {customer.unreadCount > 0 && (
                  <span className="unread-badge">{customer.unreadCount}</span>
                )}
                {unreadCount > 0 && (
                  <span className="conversation-badge" title="Unread conversations">{unreadCount}</span>
                )}
                <div className="customer-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="action-btn edit-btn"
                    onClick={(e) => handleEditCustomer(e, customer)}
                    title="Edit Customer"
                    aria-label={`Edit ${customer.name || 'customer'}`}
                  >
                    <FiEdit size={14} />
                  </button>
                  <button
                    type="button"
                    className="action-btn delete-btn"
                    onClick={(e) => handleDeleteCustomer(e, customer)}
                    title="Delete Customer"
                    aria-label={`Delete ${customer.name || 'customer'}`}
                  >
                    <FiTrash2 size={14} />
                  </button>
                  <button
                    type="button"
                    className="action-btn chat-btn primary-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChatClick(customer.phoneNumber || customer.phone);
                    }}
                    title="Start Chat"
                    aria-label={`Start chat with ${customer.name || 'customer'}`}
                  >
                    <FiMessageSquare size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

        {/* Pagination */}
        <div className="customers-pagination" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px'}}>
          <div style={{color: '#6b7280', fontSize: 14}}>
            Showing {customers.length} of {total} contacts
          </div>
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <button
              className="filter-btn"
              onClick={() => setCurrentPage(Math.max(1, (pagination?.page || currentPage) - 1))}
              disabled={(pagination?.page || currentPage) <= 1}
            >
              Prev
            </button>
            {Array.from({ length: pagination?.pages || 1 }, (_, i) => i + 1).slice(0, 10).map((p) => (
              <button
                key={p}
                className={`filter-btn ${p === (pagination?.page || currentPage) ? 'active' : ''}`}
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              className="filter-btn"
              onClick={() => setCurrentPage(Math.min(pagination?.pages || 1, (pagination?.page || currentPage) + 1))}
              disabled={(pagination?.page || currentPage) >= (pagination?.pages || 1)}
            >
              Next
            </button>
          </div>
        </div>

      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCustomer(null);
        }}
        onSave={handleSaveCustomer}
        customer={editingCustomer}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={customerToDelete?.name || ''}
      />
    </div>
  );
};

export default Customers;

